import { NfcCardModel } from '../../api/autogenerated';
import { PaymentMethodDetails } from '../../types/PaymentDetails';
import { Profile } from '../../types/Profile';
import { NFCCardRequestObject } from '../../types/requests/NFCCardRequestObject';
import { PayoutFeesResponse } from '../../types/responses/PayoutFeesResponse';
import { UserProfile } from '../../types/UserProfile';

export abstract class IProfileManagementService {
  abstract getLogoImage(fileId: string): Promise<Blob>;
  abstract getAllActiveProfiles(): Promise<Profile[]>;
  abstract getAllProfiles(): Promise<Profile[]>;
  abstract setProfile(profile: Profile): Promise<Profile>;
  abstract getProfile(forceUpdate?: boolean): Promise<Profile>;
  abstract getUserId(): Promise<string>;
  abstract updateProfile(profile: Partial<Profile>): Promise<Profile>;
  abstract removeCustomerProfilePicture(
    profile: Partial<Profile>,
  ): Promise<Profile>;
  abstract updateUserProfile(
    userProfile: Partial<UserProfile>,
  ): Promise<UserProfile>;
  abstract getPayoutFees(
    businessId: string,
    sellVolume: number,
    sellCurrency: string,
  ): Promise<PayoutFeesResponse>;
  abstract postPayoutsPreview(
    businessId: string,
    invoiceId: string,
    reconciliationAmount: number,
  ): Promise<string | null>;
  abstract getConversionRate(
    sellCurrency: string,
    buyCurrency: string,
  ): Promise<{
    userConversionRate: number;
    midMarketRate: number;
  }>;
  abstract addPaymentMethod(
    paymentMethod: PaymentMethodDetails,
  ): Promise<Profile>;
  abstract removePaymentMethod(accountNumber: string): Promise<Profile>;
  abstract setDefaultBankAccount(bankAccountId: string): Promise<Profile>;
  /**
   * Fetches the customer's bank accounts directly (GET /api/v1/bank_accounts),
   * bypassing the profile cache — for screens (e.g. the withdrawal modal) that need
   * the latest account list without force-refreshing the rest of the profile.
   */
  abstract getBankAccounts(customerId: string): Promise<PaymentMethodDetails[]>;
  abstract getNfcCardDetails(
    userId: string,
  ): Promise<{ success: boolean; data: NfcCardModel; error: string }>;
  abstract createOrUpdateNfcCard(
    userId: string,
    body: NFCCardRequestObject,
  ): Promise<{ success: boolean; data: NfcCardModel; error: string }>;
  abstract getUserProfile(): Promise<UserProfile>;
  abstract sendOTPToUserPhoneNumber(phoneNumber: string): Promise<boolean>;
  abstract verifyOTP(phoneNumber: string, otp: string): Promise<boolean>;
  abstract enableAutoWithdrawal(
    customerProfile: Partial<Profile>,
  ): Promise<boolean>;
  abstract disableAutoWithdrawal(
    customerProfile: Partial<Profile>,
  ): Promise<boolean>;
  abstract setAutoWithdrawalConsent(
    customerProfile: Partial<Profile>,
  ): Promise<boolean>;
  abstract isContextCustomerActive(customerId: string): Promise<boolean>;
}
